import React, { FC, useCallback, useMemo, useState } from 'react';
import styles from './index.module.scss';
import { Button, Skeleton, Table, Tooltip } from 'src/common';
import { TableRowSelection, ColumnType } from 'antd/lib/table/interface';
import { ICallOff, EmailStatus } from 'src/models/callOffs/types';
import { useLoadInboxEmails, useCallOffsSearch, useOpenCallOffPreview } from 'src/models/callOffs/hooks';
import { ColumnProps } from 'antd/lib/table';
import { useTranslation } from 'react-i18next';
import { CallOffsSubCategory, ProcurementsDisplay } from 'src/models/procurements/types';
import { useCurrentSidebar } from 'src/shared/InfoSidebar/hooks';
import { GetComponentProps } from 'rc-table/lib/interface';
import CallOffsSearch from './CallOffsSearch';
import CallOffsTitle from '../CallOffsTitle';
import { LoadMoreButton } from 'src/shared';
import { useFetchMore } from 'src/common/infinityScroll';
import { useBoolean } from 'react-use';
import CallOffsEmptySearch from './CallOffsEmptySearch';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip } from '@fortawesome/pro-light-svg-icons';
import CallOffsActionsMultiple from './CallOffsActionsMultiple';
import CallOffsActionsSingle from './CallOffsActionsSingle';
import ProcurementsOverviewPublished from 'src/pages/TendersPage/TendersList/Published';
import useSelectedCallOffs from 'src/reactiveVars/SelectedCallOffsVar';
import RowComponent from './Dnd';
import useOverviewMode from 'src/reactiveVars/overviewModeVar';

const components = {
  body: {
    row: RowComponent
  }
};

function useTableColumns(
  onOpenCallOffPreview: (callOff?: { id: string; isRead: boolean }) => void
): ColumnProps<ICallOff>[] {
  const { t } = useTranslation();
  return useMemo(() => {
    return [
      {
        title: <CallOffsActionsMultiple />,
        dataIndex: 'actions',
        key: 'actions',
        className: styles.colActions,
        onCell: () => ({
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
          }
        })
      },
      {
        title: t('CallOffs.received'),
        dataIndex: 'received',
        key: 'received',
        ellipsis: true,
        className: styles.colReceived,
        sorter: (a, b) => a.date.toString().localeCompare(b.date.toString()),
        sortDirections: ['descend', 'ascend'],
        render: (_, record) => {
          // TODO: switch to dayjs relativeTime plugin
          return <ProcurementsOverviewPublished published={record.date} />;
        }
      },
      {
        title: t('CallOffs.subject'),
        dataIndex: 'subject',
        key: 'subject',
        ellipsis: true,
        className: styles.colSubject,
        sorter: (a, b) => (a.subject && b.subject ? a.subject.localeCompare(b.subject, 'sv') : -1),
        sortDirections: ['descend', 'ascend'],
        render: (_, record) => {
          return (
            <span>
              <Tooltip title={record.subject ?? `(${t('CallOffs.noSubject')})`}>
                {record.subject ?? `(${t('CallOffs.noSubject')})`}
              </Tooltip>
            </span>
          );
        }
      },
      {
        title: t('CallOffs.sender'),
        dataIndex: 'originalSender',
        key: 'originalSender',
        ellipsis: true,
        className: styles.colSender,
        sorter: (a, b) =>
          a.originalSender && b.originalSender ? a.originalSender.localeCompare(b.originalSender, 'sv') : -1,
        sortDirections: ['descend', 'ascend'],
        render: (_, record) => {
          return <span>{record.originalSender}</span>;
        }
      },
      {
        title: '',
        key: 'icons',
        dataIndex: 'id',
        onCell: () => ({
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
          }
        }),
        render: (_, record) => {
          const { id, isAttachment, isRead } = record;
          return (
            <>
              <ul className={styles.infoActions}>
                <li
                  className={classNames(styles.infoAction, styles.attachment, {
                    [styles.isHidden]: !isAttachment
                  })}
                >
                  <Button
                    className={styles.attachmentsButton}
                    type={'text'}
                    onClick={() => onOpenCallOffPreview({ id, isRead })}
                    icon={<FontAwesomeIcon icon={faPaperclip} />}
                  />
                </li>
              </ul>
            </>
          );
        },
        className: styles.colInfoActions
      }
    ];
  }, [onOpenCallOffPreview, t]);
}

export const CallOffsList: FC = () => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const { t } = useTranslation();

  const procurementTabInfo = useOverviewMode();

  const currentTab: CallOffsSubCategory | undefined = useMemo(() => {
    return procurementTabInfo && procurementTabInfo.tab === ProcurementsDisplay.CALL_OFFS
      ? procurementTabInfo.subCategory
      : undefined;
  }, [procurementTabInfo]);

  const [selectedCallOffs, setSelectedCallOffs] = useSelectedCallOffs();
  const onOpenCallOffPreview = useOpenCallOffPreview();

  const searchVars = useCallOffsSearch();

  const { data: callOffsData, loading, fetchMore } = useLoadInboxEmails(searchVars);
  const isTargetTab = useMemo(() => {
    return searchVars.statuses?.some(status => status === EmailStatus.UNHANDLED);
  }, [searchVars.statuses]);
  const callOffs = useMemo(() => callOffsData?.callOffs || [], [callOffsData?.callOffs]);

  const columns: ColumnProps<ICallOff>[] = useTableColumns(onOpenCallOffPreview);
  const onRow: GetComponentProps<ICallOff> = useCallback(
    record => {
      const { id, isRead } = record;
      return {
        record,
        tabIndex: 0,
        onClick: () => onOpenCallOffPreview({ id, isRead }),
        onKeyPress: () => onOpenCallOffPreview({ id, isRead })
      };
    },
    [onOpenCallOffPreview]
  );

  const onSelectChange = useCallback(
    selectedKeys => {
      setSelectedCallOffs(callOffs.filter(callOff => selectedKeys.some((key: string) => key === callOff.id)));
    },
    [callOffs, setSelectedCallOffs]
  );
  const rowSelectionConfig: TableRowSelection<ICallOff> = {
    onChange: onSelectChange,
    preserveSelectedRowKeys: false,
    selectedRowKeys: selectedCallOffs.map(callOff => callOff.id),
    renderCell: (_, record, __, originNode) => (
      <div className={styles.colActions}>
        {originNode}
        <CallOffsActionsSingle callOff={record} />
      </div>
    )
  };

  const filteredData = useMemo(
    () => (isTargetTab ? callOffs.filter(el => !el.isExcluded) : callOffs),
    [callOffs, isTargetTab]
  );
  const total = callOffsData?.count || 0;
  const hasMore = callOffs.length < total;

  const [fetchMoreLoading, setFetchMoreLoading] = useBoolean(false); // Apollo client has a bug https://github.com/apollographql/apollo-client/issues/6354

  const loadMore = useCallback(
    (variables: { from: number }) => {
      setFetchMoreLoading(true);
      return fetchMore({
        variables
      }).finally(() => setFetchMoreLoading(false));
    },
    [fetchMore, setFetchMoreLoading]
  );

  const onLoadMore = useFetchMore({ from: filteredData.length || 0 }, loadMore);

  const isEmptySearch = useMemo(() => {
    return !callOffs.length && searchVars.keywords && !!searchVars.keywords.length;
  }, [callOffs.length, searchVars.keywords]);

  const selectionIsActive = selectedCallOffs.length > 0;
  const onHeaderRow: GetComponentProps<readonly ColumnType<ICallOff>[]> = useCallback(() => {
    return {
      className: classNames(styles.headerRow, { [styles.selectionIsActive]: selectionIsActive })
    };
  }, [selectionIsActive]);
  const currentSidebar = useCurrentSidebar();

  const rowClassName = useCallback(
    (record: ICallOff): string =>
      classNames(styles.row, {
        'ant-table-row-selected': record.id === currentSidebar?.id,
        [styles.isRead]: record.isRead,
        [styles.hidden]: isTargetTab && record.isExcluded,
        [styles.selectionIsActive]: selectionIsActive,
        [styles.isRejected]: record.isRejected
      }),
    [currentSidebar, isTargetTab, selectionIsActive]
  );

  return (
    <div className={styles.container}>
      <CallOffsTitle />
      {currentTab && <CallOffsSearch currentTab={currentTab} />}
      <div className={styles.content} ref={setContainer}>
        {loading && !callOffs ? (
          <Skeleton loading active />
        ) : (
          <>
            {isEmptySearch ? (
              <CallOffsEmptySearch />
            ) : (
              <Table
                columns={columns}
                dataSource={callOffs}
                onRow={onRow}
                rowKey={'id'}
                rowClassName={rowClassName}
                className={styles.table}
                rowSelection={rowSelectionConfig}
                onHeaderRow={onHeaderRow}
                size={'large'}
                components={components}
                locale={{
                  emptyText: <></>,
                  triggerDesc: t('Common.clickToSortDescending'),
                  triggerAsc: t('Common.clickToSortAscending'),
                  cancelSort: t('Common.clickToCancelSorting')
                }}
                footer={() => (
                  <LoadMoreButton
                    onLoadMore={onLoadMore}
                    isLoading={fetchMoreLoading}
                    hasMore={hasMore}
                    scrollContainer={container}
                  />
                )}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CallOffsList;
